// Loose-FK JOIN snippet emitter (Phase 5).
//
// Surfaces user-declared loose foreign keys (`DatabaseMetaInfo.
// looseRelationships`) as JOIN candidates in the SQL editor's suggest widget,
// exactly like real FK constraints. This is the *user-facing* seam: the
// dialect resolvers iterate `snapshot.foreignKeys` → `findApplicableFks` →
// `generateFkJoinSnippet`; this module adds an equivalent pass over the loose
// relationships so they appear in the same Monaco completion list.
//
//  • Plain loose rels (no `sourcePath`) are converted to a single-column
//    `FkEdge` and run through the *same* `findApplicableFks` /
//    `generateFkJoinSnippet` path as real FKs — identical snippet shape,
//    alias derivation, and direction handling, for free.
//  • JSON-path loose rels (`sourcePath` set) cannot be a plain `FkEdge`
//    (the source side is a JSON-extract expression, not a bare column), so
//    they get a dedicated snippet — built by the sibling module
//    `looseJsonPathJoinSnippet.ts` (split out to keep both files under the
//    repo's 200-line hard cap).
//  • A plain loose rel that exactly mirrors an existing real `FkEdge` is
//    de-duped so the user does not see the same JOIN twice — unless it
//    carries a `sourceFilter` / `targetFilter`: those are ANDed onto the ON
//    (`looseFkJoinFilters.ts`), which the real FK's JOIN would drop.
//  • A declaration names tables and columns its own way (`customer_id` for
//    Oracle's `CUSTOMER_ID`). With the resolver's `resolveBinding`, tables are
//    matched by relation identity (`fkEndpointMatch.ts`), and every declared
//    column is written in the catalog spelling of the table it lives on
//    (`declaredCatalogSpelling`) — never the declaration's own spelling
//    delimited, which names no column on a folding engine.

import type {
  AliasBinding,
  CompletionCandidate,
  DatabaseEngineId,
  FkEdge,
  TableMeta,
} from '../../../core/engines';
import type { LooseRelationship } from '@/types/electron';

import { fkCandidateIsSource, findApplicableFks, generateFkJoinSnippet, schemaMatches } from './fkJoinSnippet';
import { makeFkEndpointMatcher } from './fkEndpointMatch';
import { edgeMirrorsRealFk, looseRelToFkEdge, withCatalogColumns } from './looseFkEdge';
import { looseRelHasFilters, withLooseFilters, type FilterColumnType } from './looseFkJoinFilters';
import {
  generateLooseJsonPathJoinSnippet,
  looseFkDetail,
  looseJsonPathBoundSide,
} from './looseJsonPathJoinSnippet';

export { looseRelToFkEdge } from './looseFkEdge';

/**
 * Minimal structural table shape the emitter reads. Accepts both
 * `TableMeta` and the dialect resolvers' local `SnapshotTable` (whose `kind`
 * is a bare `string`) without a cast — identity + kind, and the columns when
 * the resolver has them.
 */
type EmitTable = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly kind: string;
  /** The declared columns' catalog spelling and the date filter literals' types (name + type is all that is read). */
  readonly columns?: readonly FilterColumnType[];
};

type EmitArgs = {
  readonly out: CompletionCandidate[];
  readonly bindings: readonly AliasBinding[];
  readonly tables: readonly EmitTable[];
  readonly looseRels: readonly LooseRelationship[];
  /** Real FK edges from the schema snapshot — used for mirror de-dupe. */
  readonly realFks: readonly FkEdge[];
  readonly engine: DatabaseEngineId;
  /** The resolver's rule for the table a bound relation names — relation identity (`fkEndpointMatch.ts`). */
  readonly resolveBinding?: (binding: AliasBinding) => EmitTable | undefined;
};

/**
 * Emit one FK-join snippet candidate per (bound alias × loose rel) pair that
 * connects, mirroring `emitFkJoinSnippets`'s structure. Plain rels reuse the
 * real-FK generator; `sourcePath` rels get the JSON-extract ON clause.
 */
export function emitLooseRelJoinSnippets(args: EmitArgs): void {
  const { out, bindings, tables, looseRels, realFks, engine, resolveBinding } = args;
  if (looseRels.length === 0) return;
  const match = resolveBinding ? makeFkEndpointMatcher(tables, resolveBinding) : undefined;

  const existingAliases = new Set<string>(
    bindings.map((b) => b.alias.toLowerCase()),
  );
  const seen = new Set<string>();
  // Per declaration, not per (binding × table): its edge, and whether it
  // mirrors a real FK (a scan of every real FK — per candidate table that was
  // O(tables × FKs) per keystroke: `tenantSchemaFkJoin.bench.ts`).
  const plainEdges = new Map<LooseRelationship, { readonly edge: FkEdge; readonly mirrorsReal: boolean } | null>();
  const plainEdgeOf = (rel: LooseRelationship) => {
    if (!plainEdges.has(rel)) {
      const edge = looseRelToFkEdge(rel);
      plainEdges.set(rel, edge && { edge, mirrorsReal: !looseRelHasFilters(rel) && edgeMirrorsRealFk(realFks, edge, match) });
    }
    return plainEdges.get(rel)!;
  };

  for (const binding of bindings) {
    if (
      binding.kind === 'cte' ||
      binding.kind === 'subquery' ||
      binding.kind === 'derived'
    ) {
      continue;
    }
    // The bound table's own columns (catalog spellings, types for date filter literals).
    const boundColumns = (
      match
        ? match.bindingTable(binding)
        : tables.find(
            (b) => b.name.toLowerCase() === binding.relation.toLowerCase() && schemaMatches(b.schema, binding.schema),
          )
    )?.columns;
    for (const t of tables) {
      const candidate: TableMeta = {
        name: t.name,
        schema: t.schema,
        database: t.database,
        kind: (t.kind === 'view' ? 'view' : 'table') as TableMeta['kind'],
        columns: [],
      };

      for (const rel of looseRels) {
        // Qualify by SCHEMA (not database) to mirror the real-FK emitter
        // (`emitFkJoinSnippets`); a database-keyed dedupe collapses
        // same-named tables across schemas into one candidate. Spellings kept:
        // case-distinct tables are distinct candidates.
        const dedupeKey = `${binding.alias.toLowerCase()}|${t.schema ?? ''}.${t.name}|${rel.id}`;
        if (seen.has(dedupeKey)) continue;

        if (rel.sourcePath) {
          const snippet = generateLooseJsonPathJoinSnippet({
            binding,
            candidate,
            rel,
            existingAliases,
            engine,
            match,
            columns: { candidate: t.columns, bound: boundColumns },
          });
          if (snippet) {
            seen.add(dedupeKey);
            // The filters follow the side the builder joined on (schema-aware).
            const boundIsSource = looseJsonPathBoundSide(binding, candidate, rel, match) === 'source';
            out.push(
              withLooseFilters(snippet, rel, binding, candidate, !boundIsSource, existingAliases, engine, {
                candidate: t.columns,
                bound: boundColumns,
              }),
            );
          }
          continue;
        }

        const plain = plainEdgeOf(rel);
        if (!plain) continue;
        const { edge } = plain;
        if (plain.mirrorsReal) {
          seen.add(dedupeKey);
          continue;
        }
        const applicable = findApplicableFks(binding, candidate, [edge], match);
        for (const fk of applicable) {
          seen.add(dedupeKey);
          // The filters follow the direction the ON equality was built in —
          // relation identity with schemas, never the bare table name (which
          // matches both ways for `a.nodes → b.nodes`).
          const candidateIsSource = fkCandidateIsSource(binding, candidate, fk, match) === true;
          // Each declared column in the catalog spelling of its own side's table.
          const catalogFk = withCatalogColumns(fk, candidateIsSource ? t.columns : boundColumns, candidateIsSource ? boundColumns : t.columns, engine);
          // A loose relationship is not a constraint: its own detail, never the
          // real-FK wording with the relationship's internal id.
          const snippet = {
            ...generateFkJoinSnippet({ from: binding, candidate, fk: catalogFk, existingAliases, engine, match }),
            detail: looseFkDetail(rel),
          };
          out.push(
            withLooseFilters(
              snippet,
              rel,
              binding,
              candidate,
              candidateIsSource,
              existingAliases,
              engine,
              { candidate: t.columns, bound: boundColumns },
            ),
          );
        }
      }
    }
  }
}
